import React, { useState, useEffect, useRef } from 'react';
import { Button, FormGroup, Input, Label } from 'reactstrap';
import { CspModal, CspModalBody, CspModalFooter, CspModalHeader } from '../CspModal';
import general_fetch from '../../utils/fetch';
import { is_valid_gst, market_of_store, canonicalise_for_market } from '../ZohoDetailsMissingModal/gst_validation';
import {
	derive_sides_to_update,
	build_finalise_body,
	describe_finalise_outcome,
	extract_finalise_details,
	is_invalid_gst_error,
} from '../ZohoDetailsMissingModal/gst_mismatch';

/**
 * Shown when an org HAS a zoho_contact_id but the GST we hold and the GST Zoho holds
 * disagree. The CS user picks one side or types a corrected value; we persist it to
 * whichever side is stale via csp/finalise_org_gst, then hand control back so the
 * download proceeds.
 *
 * Sibling of ZohoDetailsMissingModal, which covers the other case — no contact id at
 * all. Same button, different precondition; the parent decides which one opens.
 *
 * The parent owns the download — this component never calls get_zoho_estimate.
 *
 * NOTE on the caller contract: after a successful finalise, the parent MUST call
 * get_zoho_estimate WITHOUT gst_number/zoho_contact_id params. Those params arm a
 * speculative write-then-rollback inside get_zoho_estimate whose "previous" value would
 * be the PRE-finalise GST, so a later PDF failure would revert exactly the value we
 * just committed while Zoho kept the new one — reintroducing the divergence this modal
 * exists to remove. See the plan's hazard 4c.
 */
const ZohoGstMismatchModal = ({ open, toggle, store_id, store_info, mismatch, order_id, on_resolved, on_skip }) => {
	// 'backend' | 'zoho' | 'custom'. Deliberately no default: the dump can be up to a
	// week old, so pre-selecting a side would invite a blind confirm of a stale value.
	const [choice, set_choice] = useState('');
	const [custom_gst, set_custom_gst] = useState('');
	const [submitting, set_submitting] = useState(false);
	const [error_text, set_error_text] = useState('');
	// A ref, not state: the latch must take effect within a single click's handler,
	// before React has re-rendered.
	const submitted = useRef(false);

	const backend_gst = (mismatch && mismatch.backend_gst) || '';
	const zoho_gst = (mismatch && mismatch.zoho_gst) || '';
	const zoho_name = (mismatch && mismatch.zoho_name) || '';
	const org_name = (store_info && store_info.name) || '';
	// Via the shared helper so this validates against exactly the field the endpoint
	// derives its market from. See market_of_store.
	const market = market_of_store(store_info);
	// A side that already landed in an earlier attempt must not be rewritten on retry:
	// the endpoint would log a second audit row for a change that already happened, and
	// for Zoho it is a wasted API call. Only what actually failed is retried.
	const [landed, set_landed] = useState({ backend: false, zoho: false });

	// Reset on open so a previous run's choice never leaks into a new one. Keyed on
	// `open` alone: store_info is refetched while this modal can still be mounted, and
	// depending on it would wipe what the user typed mid-flow when that refetch lands.
	useEffect(() => {
		if (!open) return;
		set_choice('');
		set_custom_gst('');
		set_submitting(false);
		set_error_text('');
		set_landed({ backend: false, zoho: false });
		submitted.current = false;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	// A typed value is canonicalised to exactly the form csp/finalise_org_gst will
	// PERSIST, not merely the form it accepts. is_valid_gst canonicalises internally
	// before testing, so "29 AAXCS2582B 1ZW" validates; submitting that verbatim would
	// (a) make derive_sides_to_update compare a punctuated string against a clean
	// backend GST and demand a write that changes nothing, and (b) leave the value we
	// told the user we sent different from the one the endpoint stored, which is the
	// dirty-data divergence this modal exists to remove. India only — elsewhere
	// gst_number holds VAT / sales-tax ids whose punctuation is part of the identifier.
	// Mirrors the endpoint's own gst_to_persist.
	const chosen_gst = choice === 'backend' ? backend_gst
		: choice === 'zoho' ? zoho_gst
		: canonicalise_for_market(custom_gst, { market });

	// Validate the exact value we will submit, so the check and the payload cannot
	// diverge. Canonicalising first is a no-op for is_valid_gst (it canonicalises
	// internally) but keeps the two in lockstep if either rule ever changes.
	const custom_valid = is_valid_gst(chosen_gst, { market });
	const can_submit = Boolean(choice) && (choice !== 'custom' || custom_valid) && !submitting;

	const on_confirm = async () => {
		if (!can_submit) return;
		// Without this latch a fast double-click fires two finalise calls, which
		// double-logs the audit row.
		if (submitted.current) return;
		submitted.current = true;
		set_submitting(true);
		set_error_text('');

		const needed = derive_sides_to_update(chosen_gst, { backend_gst, zoho_gst });
		// Drop any side a previous attempt already committed, so a retry after a partial
		// failure targets only the side that is still stale.
		const sides = {
			update_backend: needed.update_backend && !landed.backend,
			update_zoho: needed.update_zoho && !landed.zoho,
		};
		// store_id, not store_info.id: store_id is the prop the Dues tab is keyed on and
		// is what the sibling estimate call already sends, while store_info is fetched
		// separately and can lag behind an org switch.
		const body = build_finalise_body(order_id, store_id, chosen_gst, sides);
		// Nothing to write — the chosen value already sits on both sides. Skip the call
		// rather than send one the endpoint would reject with INVALID_PARAMS.
		if (!body) {
			set_submitting(false);
			toggle();
			on_resolved({ updated: false });
			return;
		}

		try {
			await general_fetch({ url: 'csp/finalise_org_gst', body });
			set_submitting(false);
			toggle();
			on_resolved({ updated: true, gst_number: chosen_gst });
		} catch (err) {
			console.error('could not finalise org gst', err);
			set_submitting(false);
			// Re-arm: every failure below is one the CS user can act on and retry.
			submitted.current = false;

			if (is_invalid_gst_error(err)) {
				set_error_text('This GST number was rejected as invalid. Please check it and try again.');
				return;
			}
			// A one-sided result is NOT success — the two systems still disagree, which
			// is the exact state this modal exists to remove. Record what landed so the
			// retry targets only the side that failed, and name that side to the user.
			const details = extract_finalise_details(err);
			if (details) {
				set_landed({
					backend: Boolean(details.backend_updated),
					zoho: Boolean(details.zoho_updated),
				});
				// The user can always leave with their PDF — say so, or a repeatedly
				// failing Zoho looks like the download itself is broken.
				set_error_text(`${describe_finalise_outcome(details).message} You can also skip and download the invoice.`);
				return;
			}
			set_error_text('Could not update the GST. Please try again.');
		}
	};

	// Proceeding without reconciling must remain possible: this gates a revenue-path
	// button, and a CS user who cannot resolve the GST right now still needs the PDF.
	//
	// This is ALSO what dismissing the modal does — the × and a backdrop/Esc close are
	// the natural "get me out of here" affordance, and the user arrived here by asking
	// for a PDF. Wiring dismissal to a bare close would end the flow with no download,
	// no toast and no error: the click would simply do nothing. Reconciling is the
	// optional step; the download is the thing that was asked for.
	const on_dismiss = () => {
		if (submitting) return;
		toggle();
		// If an earlier attempt already committed the backend side, the org's GST really
		// did change — leaving via skip would download against a stale store_info. Report
		// it as resolved so the parent refetches, even though the reconciliation itself
		// did not fully succeed.
		//
		// No gst_number: `chosen_gst` tracks the CURRENT selection, which the user is free
		// to change after a partial failure, so it need not be the value that actually
		// landed. `updated` is the whole contract here — it tells the parent to refetch
		// store_info, which is where the authoritative value comes from.
		if (landed.backend) {
			on_resolved({ updated: true });
			return;
		}
		on_skip();
	};

	const option_row = (value, label, gst_value, sub_label) => (
		<FormGroup check className='mb-2'>
			<Label check className='d-flex align-items-start' style={{ cursor: 'pointer' }}>
				<Input
					type='radio'
					name='gst_mismatch_choice'
					checked={choice === value}
					onChange={() => { set_choice(value); set_error_text(''); }}
				/>
				<span className='ml-2'>
					<span className='font-weight-bold small d-block'>{label}</span>
					<span style={{ fontFamily: 'monospace' }}>{gst_value}</span>
					{sub_label ? <span className='d-block text-muted small'>{sub_label}</span> : null}
				</span>
			</Label>
		</FormGroup>
	);

	return (
		<CspModal open={open} toggle={on_dismiss} size='md'>
			<CspModalHeader title='GST mismatch' toggle={on_dismiss} />
			<CspModalBody>
				<div className='mb-3'>
					The GST number on Infurnia does not match the one on this org's Zoho contact.
					Pick the correct value — it will be updated on whichever side is out of date.
				</div>

				{option_row('backend', `Infurnia — ${org_name}`, backend_gst)}
				{option_row('zoho', `Zoho — ${zoho_name}`, zoho_gst, 'From the weekly Zoho contacts dump')}

				<FormGroup check className='mb-2'>
					<Label check className='d-flex align-items-start' style={{ cursor: 'pointer' }}>
						<Input
							type='radio'
							name='gst_mismatch_choice'
							checked={choice === 'custom'}
							onChange={() => { set_choice('custom'); set_error_text(''); }}
						/>
						<span className='ml-2 font-weight-bold small'>Neither — enter the correct GST number</span>
					</Label>
				</FormGroup>
				{choice === 'custom' ? (
					<FormGroup className='ml-4'>
						<Input
							id='gst_mismatch_custom'
							value={custom_gst}
							onChange={e => { set_custom_gst(e.target.value); set_error_text(''); }}
							placeholder='Enter GST number'
						/>
						{custom_gst && !custom_valid ? (
							<span className='text-danger small'>Not a valid GST number for this org.</span>
						) : null}
					</FormGroup>
				) : null}

				{/* The dump is up to a week old, so a Zoho correction made since then is not
				    reflected above. Say so rather than presenting it as live. */}
				<div className='text-muted small mt-3'>
					Zoho values come from the weekly contacts dump and may be up to a week old.
				</div>

				{error_text ? <div className='text-danger small mt-2'>{error_text}</div> : null}
			</CspModalBody>
			<CspModalFooter>
				<Button className='btn-sm' color='secondary' disabled={submitting} onClick={on_dismiss}>
					Skip and download
				</Button>
				<Button className='btn-sm' color='primary' disabled={!can_submit} onClick={on_confirm}>
					{submitting ? 'Please wait...' : 'Update and download'}
				</Button>
			</CspModalFooter>
		</CspModal>
	);
};

export default ZohoGstMismatchModal;
